/**
 * Upload image to Cloudinary
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} - The secure URL of the uploaded image
 * @throws {Error} - If upload fails or file is invalid
 */
const uploadToCloudinary = async (file) => {
    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

    if (!file) {
        throw new Error('No file provided');
    }

    if (!validImageTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please upload an image (JPEG, PNG, WebP, or GIF)');
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
        throw new Error('File size too large. Maximum size is 10MB');
    }

    // Cloudinary configuration
    const cloudName = 'dv4dvemp7';
    const uploadPreset = 'sociopedia-images';
    const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

    // Create FormData
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', uploadPreset);

    try {
        // Upload to Cloudinary
        const response = await fetch(uploadUrl, {
            method: 'POST',
            body: formData,
        });

        // Check if response is ok
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
                errorData.error?.message ||
                `Upload failed with status: ${response.status}`
            );
        }

        // Parse response
        const data = await response.json();

        // Validate response has secure_url
        if (!data.secure_url) {
            throw new Error('Upload successful but no image URL received');
        }

        // Return the secure URL
        return data.secure_url;
    } catch (error) {
        // Re-throw with more context
        if (error.message.includes('fetch')) {
            throw new Error('Network error. Please check your internet connection');
        }
        throw error;
    }
};

export default uploadToCloudinary;
